/**
 * SelectQuestion Component
 * Handles dropdown select menus for questions with many options
 */

import { QuestionOption } from '../../../types/formConfig'

interface SelectQuestionProps {
  questionId: string
  options: QuestionOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  error?: string
}

const SelectQuestion = ({
  questionId,
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  required = false,
  error
}: SelectQuestionProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className="w-full max-w-xl">
      <div className="relative">
        <select
          id={questionId}
          name={questionId}
          value={value}
          onChange={handleChange}
          required={required}
          autoFocus
          className={`
            w-full px-6 py-4 text-lg border-2 rounded-lg appearance-none cursor-pointer transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
            ${value ? 'text-dark-plum' : 'text-gray-400'}
            ${
              error
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 bg-white focus:border-primary-green'
            }
          `}
          aria-invalid={!!error}
          aria-describedby={error ? `${questionId}-error` : undefined}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {option.description ? ` - ${option.description}` : ''}
            </option>
          ))}
        </select>

        {/* Custom dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          id={`${questionId}-error`}
          className="mt-2 text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <svg
            className="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}
    </div>
  )
}

export default SelectQuestion
